import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ExpandableDescriptionProps {
  description?: string;
  maxLength?: number;
  className?: string;
  initiallyExpanded?: boolean;
  maxHeight?: number;
  translations?: {
    showMore?: string;
    showLess?: string;
  };
}

export const ExpandableDescription: React.FC<ExpandableDescriptionProps> = ({
  description,
  maxLength = 200,
  className = '',
  initiallyExpanded = false,
  maxHeight = 100,
  translations = {}
}) => {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const [needsToggle, setNeedsToggle] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const t = {
    showMore: translations.showMore || 'Mostra di più',
    showLess: translations.showLess || 'Mostra meno'
  };

  // Check if the description needs to be truncated
  useEffect(() => {
    if (contentRef.current) {
      // Check if we need a toggle button
      const needsButton =
        (description && description.length > maxLength) ||
        (contentRef.current.scrollHeight > maxHeight);

      setNeedsToggle(needsButton);
    }
  }, [description, maxLength, maxHeight]);

  // Don't render if there's no description
  if (!description) return null;

  return (
    <div className={`relative ${className}`}>
      <div
        ref={contentRef}
        className={`prose prose-sm dark:prose-invert max-w-none text-black/80 dark:text-white/90 transition-all duration-300 ease-in-out ${!isExpanded ? 'line-clamp-3' : ''}`}
        style={{
          maxHeight: isExpanded ? 'none' : `${maxHeight}px`,
          overflow: isExpanded ? 'visible' : 'hidden'
        }}
        dangerouslySetInnerHTML={{ __html: description }}
      />

      {needsToggle && (
        <div
          className={`text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium cursor-pointer flex items-center gap-1 pt-1 ${isExpanded ? '' : 'mt-1'}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <>
              <ChevronUp size={16} />
              {t.showLess}
            </>
          ) : (
            <>
              <ChevronDown size={16} />
              {t.showMore}
            </>
          )}
        </div>
      )}

      {!isExpanded && needsToggle && (
        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-transparent to-transparent pointer-events-none"></div>
      )}
    </div>
  );
};

export default ExpandableDescription;